import MapView from "@arcgis/core/views/MapView";
import Map from "@arcgis/core/Map";
import BasemapToggle from "@arcgis/core/widgets/BasemapToggle";
import Track from "@arcgis/core/widgets/Track";
import TileLayer from "@arcgis/core/layers/TileLayer";
import FeatureLayer from "@arcgis/core/layers/FeatureLayer";
import LayerList from "@arcgis/core/widgets/LayerList.js";

import Expand from "@arcgis/core/widgets/Expand.js";
import Graphic from "@arcgis/core/Graphic";
import SimpleFillSymbol from "@arcgis/core/symbols/SimpleFillSymbol";
import SimpleLineSymbol from "@arcgis/core/symbols/SimpleLineSymbol";
import Polygon from "@arcgis/core/geometry/Polygon";
import Polyline from "@arcgis/core/geometry/Polyline";
import Point from "@arcgis/core/geometry/Point";

import thuaTest from "../_mocks_/geometry.json";
import {
  getRanhThuaTheoHuyenXaUrl,
  getDuLieuNenUrl,
  layTimDuongUrlTheoHaHuyen,
} from "./UrlHelper";
import { registerToken } from "./ArcgisIdentityManager";
import { IDisplayGeo } from "../_types_";

export default class ArcgisHelper {
  map: Map;
  featureDat: FeatureLayer;
  mapView: MapView;
  ranhThuaUrl: string | undefined;
  // quyHoachUrl: string | undefined;
  maHuyen: string | undefined;
  maXa: string | undefined;
  featureTimDuong: FeatureLayer;
  featureGiaoThong: FeatureLayer;
  lopNenUrl: string | undefined;
  timDuongUrl: string | undefined;
  shapeFileSpatial = {
    wkt: 'PROJCS["VN_2000_BR_Vung_Tau_3deg",GEOGCS["GCS_VN_2000",DATUM["D_Vietnam_2000",SPHEROID["WGS_1984",6378137.0,298.257223563]],PRIMEM["Greenwich",0.0],UNIT["Degree",0.0174532925199433]],PROJECTION["Transverse_Mercator"],PARAMETER["False_Easting",500000.0],PARAMETER["False_Northing",0.0],PARAMETER["Central_Meridian",107.75],PARAMETER["Scale_Factor",0.9999],PARAMETER["Latitude_Of_Origin",0.0],UNIT["Meter",1.0]]',
  };
  constructor(ref: HTMLDivElement, maHuyen: string, maXa: string) {
    this.map = new Map({ basemap: "streets-vector" });
    this.mapView = new MapView({
      container: ref,
      map: this.map,
      center: [-118.805, 34.027], // Longitude, latitude
      zoom: 13, // Zoom level
    });

    this.maHuyen = maHuyen;
    this.maXa = maXa;
    this.ranhThuaUrl = `${getRanhThuaTheoHuyenXaUrl(this.maHuyen, this.maXa)}`;

    // this.quyHoachUrl = `${getQuyHoachUrl(this.maHuyen)}/rest/services`;
    this.featureDat = new FeatureLayer({ url: this.ranhThuaUrl });
    this.featureTimDuong = new FeatureLayer({
      url: `${layTimDuongUrlTheoHaHuyen(maHuyen)}`,
      title: "Tim đường",
      //displayField:'tenDuong'
    });
    this.featureGiaoThong = new FeatureLayer({
      url: `${layTimDuongUrlTheoHaHuyen(maHuyen)}/1`,
    });
    this.lopNenUrl = getDuLieuNenUrl(this.maHuyen);
    this.timDuongUrl = `${layTimDuongUrlTheoHaHuyen(maHuyen)}`;
  }

  async init(): Promise<void> {
    if (!this.maHuyen || !this.maXa) return;
    this.ranhThuaUrl = `${getRanhThuaTheoHuyenXaUrl(this.maHuyen, this.maXa)}`;
    await registerToken(this.maHuyen);
  }

  private drawPolygon = async (geometry: any, color: string = "#0984e3") => {
    const simpleFillSymbol = new SimpleFillSymbol({
      style: "solid",
      color: color || "#0984e3",
      outline: {
        color: "#0984e3",
        width: 1,
      },
    });
    const polygon = new Polygon({
      hasZ: true,
      hasM: true,
      rings: geometry.rings,
      spatialReference:
        geometry.spatialReference?.wkid || geometry.spatialReference?.wkt
          ? geometry.spatialReference
          : this.shapeFileSpatial,
      //{
      //   //wkid: geometry.wkid | 3405,
      //   wkt:geometry.
      // },
    });

    const graphic = new Graphic({
      geometry: polygon,
      symbol: simpleFillSymbol,
    });
    this.mapView.graphics.add(graphic);
  };

  private drawPolyline = async (geometry: any, color: string = "red") => {
    const simpleFillSymbol = new SimpleLineSymbol({
      style: "solid",
      color: color,
      width: 2,
    });
    const polyline = new Polyline({
      hasZ: true,
      hasM: true,
      paths: geometry.paths,
      spatialReference: { wkid: geometry.wkid },
    });
    const graphic = new Graphic({
      geometry: polyline,
      symbol: simpleFillSymbol,
    });
    this.mapView.graphics.add(graphic);
  };

  private drawPoint = async (geometry: any, color: string = "red") => {
    const simpleFillSymbol = new SimpleLineSymbol({
      style: "solid",
      color: color,
      width: 3,
    });
    const point = new Point({
      y: geometry.y,
      x: geometry.x,
      hasM: true,
      hasZ: true,
    });
    const graphic = new Graphic({
      geometry: point,
      symbol: simpleFillSymbol,
    });
    this.mapView.graphics.add(graphic);
  };

  private addGraphics = async (
    features: __esri.Graphic[],
    isLine: boolean = false
  ) => {
    try {
      this.mapView?.graphics.removeAll();
      const thuaGeo = JSON.parse(thuaTest.geometry);
      const simpleFillSymbol = new SimpleFillSymbol({
        style: isLine ? "none" : "solid",
        color: "#74b9ff",
        outline: {
          color: "#0984e3",
          width: 1,
        },
      });

      const graphics = features.map(
        (feature) =>
          new Graphic({
            geometry: feature.geometry,
            attributes: feature.attributes,
            symbol: simpleFillSymbol,
          })
      );
      this.mapView?.graphics.addMany(graphics);

      // const polygon = new Polygon({
      //     hasZ: true,
      //     hasM: true,
      //     rings: thuaGeo.rings,
      //     spatialReference: { wkid: thuaGeo.wkid },
      // });

      // const graphic = new Graphic({
      //     geometry: polygon,
      //     symbol: simpleFillSymbol,
      // });

      // this.mapView.graphics.add(graphic);

      // graphicsLayer.graphics.add(geometry);
      // this.map.add(graphicsLayer);

      // await this.mapView.goTo({target: graphic});

      if (this.mapView && features.length > 0) {
        await this.mapView.goTo(features[0].geometry.extent.expand(3));
      }
    } catch (er) {
      console.log({ er });
    }
  };

  initializeMap() {
    const layerGiaoThong = new FeatureLayer({
      url: `${this.timDuongUrl}/1`,
      title: "Giao thông",
      displayField: "tenDuong",
    });
    layerGiaoThong.visible = false;
    this.map.add(layerGiaoThong);
    const layerNen = new TileLayer({
      url: `${this.lopNenUrl}`,
      id: "lopNen",
    });

    layerNen.visible = true;
    this.map.add(layerNen);

    // const diemMoQuyHoachLayer = new TileLayer({
    //   url: this.quyHoachUrl,
    //   opacity: 0,
    // });

    // this.map.add(diemMoQuyHoachLayer);

    const layerXa = new TileLayer({
      url: `${this.ranhThuaUrl}`,
      title: "Ranh thửa",
    });
    this.map.add(layerXa);
    layerXa.when(() => this.mapView?.goTo(layerXa.fullExtent));

    this.featureDat = new FeatureLayer({
      url: `${this.ranhThuaUrl}`,
    });
    // this.map.add(this.featureDat);
    // console.log({ ranhthuaurl: this.ranhThuaUrl });

    this.map.add(this.featureTimDuong);
    const toggle = new BasemapToggle({
      view: this.mapView,
      nextBasemap: "hybrid",
    });
    this.mapView?.ui.add(toggle, "bottom-left");

    const layerList = new LayerList({
      view: this.mapView,
    });

    const layerListExpand = new Expand({
      expandIconClass: "esri-icon-layers",
      expandTooltip: "Hiện thị lớp bản đồ",
      view: this.mapView,
      expanded: false,
      content: layerList,
      group: "expandable-widgets",
      collapseTooltip: "Thu gọn lớp bản đồ",
      autoCollapse: true,
    });

    this.mapView?.ui.add([layerListExpand], "bottom-right");

    const track = new Track({
      view: this.mapView,
    });
    this.mapView?.ui.add(track, "top-left");
    return this.mapView;
  }

  searchByToThua = async (soTo: string, soThua: string) => {
    const query = {
      outFields: ["*"],
      returnGeometry: true,
      where: `SoHieuToBanDo = ${soTo} and SoThuTuThua = ${soThua}`,
    };
    const rs = await this.featureDat.queryFeatures(query);
    if (rs.features.length > 0) {
      //await this.testDrawGeometry();
      await this.addGraphics(rs.features, false);
    } else {
      alert("Không tìm thấy kết quả");
    }
  };

  drawGeometries(geometries: IDisplayGeo[]) {
    this.mapView.graphics.removeAll();
    for (let i = 0; i < geometries.length; i += 1) {
      const geo = geometries[i];
      const thuaGeo = JSON.parse(geo.geo);

      if (thuaGeo) {
        thuaGeo.rings
          ? this.drawPolygon(thuaGeo, geo.color)
          : thuaGeo.paths
          ? this.drawPolyline(thuaGeo, geo.color)
          : this.drawPoint(thuaGeo);
      }
    }
  }

  queryFeatureLayer = async (
    point: __esri.Point,
    distance: number,
    spatialRelationship: __esri.QueryProperties["spatialRelationship"],
    sqlExpression?: string
  ) => {
    const query: __esri.QueryProperties = {
      geometry: point,
      distance: distance,
      spatialRelationship,
      outFields: ["*"],
      returnGeometry: true,
      where: sqlExpression,
    };
    const featureSet = await this.featureDat.queryFeatures(query);
    await this.addGraphics(featureSet.features);
    return featureSet.features[0];
  };

  searchByTenDuong = async (tenDuong: string) => {
    const query = {
      outFields: ["*"],
      returnGeometry: true,
      where: `LOWER(tenDuong) like N'%${tenDuong.toLowerCase()}%'`,
    };
    const rs = await this.featureGiaoThong.queryFeatures(query);
    if (rs.features.length > 0) {
      await this.addGraphics(rs.features, false);
    }
  };
}
